/**
 * CANONICAL MODULE — app-platform/shared/themeApply.ts
 *
 * Copied byte-for-byte into every app that takes the theme engine (`src/lib/themeApply.ts`). The one
 * theme module that touches the DOM, and it only touches what it is handed: the document is
 * injected, so it tests against a fake. Imports only `themeMode.ts`. **Fix bugs HERE, then sync
 * outward.**
 *
 * ── What it is ───────────────────────────────────────────────────────────────────────────────────
 * `applyTheme(doc, paint)` writes design/W-theme-contract.md §5's table onto `<html>`, the same
 * writes `boot-theme.js` makes before the app loads, so the hand-over is seamless:
 *
 *   data-theme (light/dark/oled), data-scheme (light/dark), data-theme-custom (or removed),
 *   style.colorScheme, inline `--theme-*` vars, meta[name=theme-color].
 *
 * It writes **only `--theme-*` names**, never an app's own (`--accent`, `--bg-base`): an app's names
 * reach the vars through its generated alias block, so an inline app name would beat the alias (R2).
 * Any `--theme-*` it set before and is not setting now is removed, so leaving a custom theme for a
 * built-in one leaves no stale inline colour behind (the built-in palette is the stylesheet's).
 *
 * `commitTheme` is apply + mirror + `__themeLive` in the order an app wants on every change.
 */

import { schemeOf, writeMirror } from './themeMode'
import type { BootMirror, MirrorStorage, ThemeMode } from './themeMode'

/** The slice of `document` applyTheme writes to. A real `document` satisfies it. */
export interface ThemeDocument {
  documentElement: {
    setAttribute(name: string, value: string): void
    removeAttribute(name: string): void
    style: {
      setProperty(name: string, value: string): void
      removeProperty(name: string): string
      colorScheme: string
      readonly length: number
      item(index: number): string
    }
  }
  querySelector(selector: string): { setAttribute(name: string, value: string): void } | null
}

export interface ThemePaint {
  theme: ThemeMode
  customId?: string
  /** Inline `--theme-*` overrides (a custom theme's solved map, quil's ground, contently's accent). */
  vars?: Record<string, string>
  /** The page colour for meta[name=theme-color]; left alone when absent. */
  bg?: string
}

const THEME_VAR = /^--theme-[a-z0-9-]+$/

/**
 * Apply a resolved theme to the document. Throws, naming it, on a var outside `--theme-*` (a
 * programming error the tests should catch, not something to skip quietly).
 */
export function applyTheme(doc: ThemeDocument, paint: ThemePaint): void {
  const root = doc.documentElement
  const vars = paint.vars ?? {}
  for (const name of Object.keys(vars)) {
    if (!THEME_VAR.test(name)) throw new Error(`applyTheme writes only --theme-* vars, got "${name}"`)
  }
  const scheme = schemeOf(paint.theme)
  root.setAttribute('data-theme', paint.theme)
  root.setAttribute('data-scheme', scheme)
  if (paint.customId) root.setAttribute('data-theme-custom', paint.customId)
  else root.removeAttribute('data-theme-custom')
  root.style.colorScheme = scheme

  const stale: string[] = []
  for (let i = 0; i < root.style.length; i++) {
    const name = root.style.item(i)
    if (THEME_VAR.test(name) && !Object.hasOwn(vars, name)) stale.push(name)
  }
  for (const name of stale) root.style.removeProperty(name)
  for (const [name, value] of Object.entries(vars)) root.style.setProperty(name, value)

  if (paint.bg) doc.querySelector('meta[name="theme-color"]')?.setAttribute('content', paint.bg)
}

/**
 * The window flag that tells boot-theme.js to stop following system flips (the app owns it now).
 * Exported for tests only (app-platform 8.3): no app code calls it.
 */
export function markThemeLive(win: { __themeLive?: boolean }): void {
  win.__themeLive = true
}

/**
 * What an app calls on every theme change: apply, write the boot mirror (so the next cold start
 * paints the same thing on frame one), and take over from the boot script. Returns whether the
 * mirror was written (false when storage is blocked; the theme is applied either way).
 */
export function commitTheme(
  ctx: { doc: ThemeDocument; win: { __themeLive?: boolean }; storage: MirrorStorage | null | undefined; storageKey: string },
  paint: ThemePaint,
  mirror: Omit<BootMirror, 'v' | 'resolved'>,
): boolean {
  applyTheme(ctx.doc, paint)
  markThemeLive(ctx.win)
  const resolved: NonNullable<BootMirror['resolved']> = { theme: paint.theme }
  if (paint.customId) resolved.customId = paint.customId
  if (paint.vars && Object.keys(paint.vars).length > 0) resolved.vars = { ...paint.vars }
  if (paint.bg) resolved.bg = paint.bg
  return writeMirror(ctx.storage, ctx.storageKey, { v: 1, ...mirror, resolved })
}
